import React, { useState } from 'react';
import UISectionWrapper, {
  UISectionHeader,
  UISectionBody,
  UISectionFooter,
  UIFormRow,
  UIFormLabels,
  UIFormFields,
  UIFormLabel
} from '@/components/shared/UISectionWrapper';
import UISectionTitle from '@/components/shared/UISectionTitle';
import UIInput from '@/components/shared/UIInput';
import UIButton from '@/components/shared/UIButton';
import { useToast } from '@/components/shared/ToastProvider';
import { changePassword } from '@/api/auth';
import './ChangePasswordSection.scss';

export interface ChangePasswordSectionProps {
  className?: string;
}

const baseClass = 'ChangePasswordSection';

const ChangePasswordSection: React.FC<ChangePasswordSectionProps> = ({
  className = '',
}) => {
  const { showSuccess, showError } = useToast();
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');

  const handleClear = () => {
    setOldPassword('');
    setNewPassword('');
    setConfirmNewPassword('');
  };

  const handleSave = async () => {
    if (newPassword !== confirmNewPassword) {
      showError('New passwords do not match.');
      return;
    }

    try {
      await changePassword({ oldPassword, newPassword, confirmNewPassword });
      showSuccess('Password changed successfully.');
      handleClear();
    } catch (error: any) {
      const message = error?.response?.data?.message || 'An error occurred while changing the password.';
      showError(message);
    }
  };

  return (
    <UISectionWrapper
      className={`${baseClass} ${className}`}
      layout="form-horizontal"
      formPadding="large"
      labelWidth="153px"
      formGap="15px"
      align="stretch"
    >
      <UISectionHeader>
        <UISectionTitle
          title="Change your password"
          description=""
        />
      </UISectionHeader>

      <UISectionBody>
        <UIFormRow>
          <UIFormLabels>
            <UIFormLabel>Current password</UIFormLabel>
            <UIFormLabel>New password</UIFormLabel>
            <UIFormLabel>Confirm new password</UIFormLabel>
          </UIFormLabels>

          <UIFormFields>
            <UIInput
              type="password"
              placeholder="Enter current password"
              fullWidth
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
            />

            <UIInput
              type="password"
              placeholder="Enter new password"
              fullWidth
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />

            <UIInput
              type="password"
              placeholder="Confirm new password"
              fullWidth
              value={confirmNewPassword}
              onChange={(e) => setConfirmNewPassword(e.target.value)}
            />

            <div className={`${baseClass}__requirements`}>
              <div className={`${baseClass}__requirements-title`}>
                Password requirements:
              </div>
              <div className={`${baseClass}__requirements-subtitle`}>
                Ensure that these requirements are met:
              </div>
              <div className={`${baseClass}__requirements-list`}>
                Minimum 8 characters long<br/>
                At least one lowercase character<br/>
                At least one uppercase character<br/>
                At least one number, symbol, or whitespace character
              </div>
            </div>
          </UIFormFields>
        </UIFormRow>
      </UISectionBody>

      <UISectionFooter>
        <div className={`${baseClass}__buttons`}>
          <UIButton variant="primary" onClick={handleSave}>
            Save
          </UIButton>
          <UIButton variant="primary" outline onClick={handleClear}>
            Clear
          </UIButton>
        </div>
      </UISectionFooter>
    </UISectionWrapper>
  );
};

export default ChangePasswordSection;